import type { FaqAccordionItem } from '@components/FaqAccordion/FaqAccordion';

// Master list of all potential FAQ items
// Partners can reference these by index in their config
export const ALL_FAQS: FaqAccordionItem[] = [
  {
    id: 'eligibility',
    question: 'Who is eligible to participate?',
    answer: 'Adults older than 18 are eligible to participate. Certain tests may have a specific age criteria.',
  },
  {
    id: 'privacy',
    question: 'Will my personal results be shared with anyone?',
    answer: 'No. Your results are private and are only shared with you (and your healthcare provider, if you choose).',
  },
  {
    id: 'conditions',
    question: 'What conditions are screened for?',
    answer: 'We assess risk for conditions like heart disease, hereditary cancers, and other diseases.',
  },
  {
    id: 'sample-type',
    question: 'What kind of sample is needed?',
    answer: 'Members will provide a buccal (cheek swab) sample.',
  },
  {
    id: 'timeline',
    question: 'How long does it take to get my results?',
    answer: 'Typically, results are ready 4-6 weeks after your sample is received.',
  },
  {
    id: 'family-history',
    question: 'What if I have a family history of a condition?',
    // prettier-ignore
    answer: 'Genetic testing can help clarify your risk—even if you already have a family history or prior testing. If you opt for genetic counseling, they can help you understand your results in the context of your personal and family history.',
  },
  {
    id: 'after-results',
    question: 'What happens after I receive my results?',
    answer: "You'll have access to genetic counseling and can choose to share your results with your doctor.",
  },
  {
    id: 'data-protection',
    question: 'How is my data protected?',
    // prettier-ignore
    answer: 'MyOme follows strict data privacy and security standards. Your information is never sold or shared without your consent.',
  },
  {
    id: 'doctor-order',
    question: "Do I need a doctor's order?",
    // prettier-ignore
    answer: "MyOme coordinates with independent physicians for test ordering. You don't need to do anything extra. However, we encourage you to discuss your results with your personal healthcare provider to ensure you fully understand your genetic risk and any recommended next steps.",
  },
  {
    id: 'discrimination-longevity',
    question: 'Am I protected from discrimination based on my genetic information?',
    // prettier-ignore
    answer: "The Genetic Information Nondiscrimination Act (GINA) is a federal law that protects individuals from discrimination by health insurers and employers based on their genetic information. GINA prohibits these entities from using genetic test results to determine eligibility, premiums, or employment decisions. GINA does not apply to life, disability, or long-term care insurance. However, since Longevity Life members already have life insurance coverage in place, participation in MyOme's genetic testing will not affect your existing policy.",
  },
  {
    id: 'discrimination',
    question: 'Am I protected from discrimination based on my genetic information?',
    // prettier-ignore
    answer: 'The Genetic Information Nondiscrimination Act (GINA) is a federal law that protects individuals from discrimination by health insurers and employers based on their genetic information. GINA prohibits these entities from using genetic test results to determine eligibility, premiums, or employment decisions. GINA does not apply to life, disability, or long-term care insurance.',
  },
];

export const getFAQsByIndexes = (indexes: number[]): FaqAccordionItem[] => {
  return indexes.map(index => ALL_FAQS[index]).filter(Boolean);
};

// BridgeBio Family Cascade Genetic Testing has its own FAQ set: targeted CASR/ADH1 test,
// blood relatives up to 4th degree (incl. minors), no whole-genome / multi-condition framing.
export const FVT_FAQS: FaqAccordionItem[] = [
  {
    id: 'fvt-eligibility',
    question: 'Who is eligible to participate?',
    answer: (
      <>
        This program is available to blood relatives (up to 4th degree) of a patient who received a confirmed pathogenic
        or variant of uncertain significance (VUS) result in the <i>CASR</i> gene. That includes parents, siblings,
        half-siblings, children, grandparents, grandchildren, aunts and uncles, nieces and nephews, first cousins, and
        other blood relatives within that range. Both adults and minors can participate; a parent or guardian completes
        the process on behalf of a minor.
      </>
    ),
  },
  {
    id: 'fvt-conditions',
    question: 'What does this test look for?',
    answer: (
      <>
        This is a targeted test for the specific <i>CASR</i> variant(s) identified in your relative. Variants in the{' '}
        <i>CASR</i> gene can cause ADH1 (autosomal dominant hypocalcemia type 1), a genetic cause of low blood calcium.
        It does not screen for unrelated conditions.
      </>
    ),
  },
  {
    id: 'fvt-privacy',
    question: 'Will my personal results be shared with anyone?',
    answer: 'No. Your results are private and are only shared with you (and your healthcare provider, if you choose).',
  },
  {
    id: 'fvt-sample-type',
    question: 'What kind of sample is needed?',
    answer: 'You will provide a cheek swab (buccal) sample using a kit mailed to your home.',
  },
  {
    id: 'fvt-timeline',
    question: 'How long does it take to get my results?',
    answer: 'Typically, results are ready 2-4 weeks after your sample is received.',
  },
  {
    id: 'fvt-after-results',
    question: 'What happens after I receive my results?',
    answer:
      "You'll have access to genetic counseling and can choose to share your results with your doctor. If a variant is found, you may also have the opportunity to help your own eligible blood relatives get tested.",
  },
  {
    id: 'fvt-cost',
    question: 'How much does it cost?',
    answer: 'Testing and genetic counseling are offered at no-charge to eligible relatives.',
  },
  {
    id: 'fvt-data-protection',
    question: 'How is my data protected?',
    answer:
      'MyOme follows strict data privacy and security standards. Your information is never sold or shared without your consent.',
  },
  {
    id: 'fvt-doctor-order',
    question: "Do I need a doctor's order?",
    answer:
      "MyOme coordinates with independent physicians for test ordering. You don't need to do anything extra. We encourage you to discuss your results with your personal healthcare provider.",
  },
  {
    id: 'fvt-discrimination',
    question: 'Am I protected from discrimination based on my genetic information?',
    answer:
      'The Genetic Information Nondiscrimination Act (GINA) is a federal law that protects individuals from discrimination by health insurers and employers based on their genetic information. GINA prohibits these entities from using genetic test results to determine eligibility, premiums, or employment decisions. GINA does not apply to life, disability, or long-term care insurance.',
  },
];
